import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Category, type HealthFilter, type Product, type ProductImage, type ProductPayload, type ProductSkuPayload } from "@/lib/api";
import SkuComponentsEditor, { type SkuComponentForm } from "./SkuComponentsEditor";
import {
  isNewProductRoute,
  normalizeSkuComponentType,
  normalizeSkuWholesale,
  toNumberOrNull,
  type SkuComponentType,
} from "./productFormUtils";

type SkuForm = {
  skuId: string | null;
  weightLabel: string;
  weightGrams: string;
  skuDescription: string;
  oldPrice: string;
  currentPrice: string;
  isActive: boolean;
  isWholesale: boolean;
  showInCatalog: boolean;
  imageFiles: File[];
  imagePreviews: ImagePreview[];
  components: SkuComponentForm[];
};

type ImagePreview = {
  name: string;
  url: string;
};

const emptySku = (): SkuForm => ({
  skuId: null,
  weightLabel: "",
  weightGrams: "",
  skuDescription: "",
  oldPrice: "",
  currentPrice: "",
  isActive: true,
  isWholesale: false,
  showInCatalog: false,
  imageFiles: [],
  imagePreviews: [],
  components: [],
});

const emptySkuComponent = (componentType: SkuComponentType): SkuComponentForm => ({
  componentType,
  displayName: "",
  quantity: "1",
  comment: "",
});

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = isNewProductRoute(id);

  const [categories, setCategories] = useState<Category[]>([]);
  const [healthFilters, setHealthFilters] = useState<HealthFilter[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState<"draft" | "active" | "hidden">("draft");
  const [isBestseller, setIsBestseller] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [filterCategoryIds, setFilterCategoryIds] = useState<string[]>([]);
  const [healthFilterIds, setHealthFilterIds] = useState<string[]>([]);
  const [categoryCreatorOpen, setCategoryCreatorOpen] = useState(false);
  const [newCategoryLevel, setNewCategoryLevel] = useState<"root" | "child">("child");
  const [newCategoryParentId, setNewCategoryParentId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategorySlug, setNewCategorySlug] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryCreatorError, setCategoryCreatorError] = useState("");
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState("");
  const [editCategoryLevel, setEditCategoryLevel] = useState<"root" | "child">("root");
  const [editCategoryParentId, setEditCategoryParentId] = useState("");
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategorySlug, setEditCategorySlug] = useState("");
  const [editCategoryActive, setEditCategoryActive] = useState(true);
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [categoryEditorError, setCategoryEditorError] = useState("");
  const [contentBlock, setContentBlock] = useState("");
  const [skus, setSkus] = useState<SkuForm[]>([emptySku()]);
  const skusRef = useRef<SkuForm[]>(skus);
  const [deletingImageIds, setDeletingImageIds] = useState<Set<string>>(new Set());
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);

  useEffect(() => {
    skusRef.current = skus;
  }, [skus]);

  useEffect(() => {
    return () => {
      skusRef.current.forEach((sku) => {
        sku.imagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
      });
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const requests = [api.categories(), api.healthFilters(), !isNew && id ? api.product(id) : Promise.resolve(null)] as const;
    Promise.all(requests)
      .then(([nextCategories, nextHealthFilters, nextProduct]) => {
        if (!mounted) return;
        setCategories(nextCategories);
        setHealthFilters(nextHealthFilters);
        if (nextProduct) {
          setProduct(nextProduct);
          setName(nextProduct.name);
          setSlug(nextProduct.slug);
          setStatus(nextProduct.status);
          setIsBestseller(nextProduct.is_bestseller);
          setCategoryId(nextProduct.category_id || "");
          setSubcategoryId(nextProduct.subcategory_id || "");
          setFilterCategoryIds(nextProduct.filter_category_ids || []);
          setHealthFilterIds(nextProduct.health_filter_ids || []);
          setContentBlock(nextProduct.content_block || "");
          setSkus(
            nextProduct.skus.length > 0
              ? nextProduct.skus.map((sku) => ({
                  skuId: sku.sku_id,
                  weightLabel: sku.weight_label || "",
                  weightGrams: sku.weight_grams ? String(sku.weight_grams) : "",
                  skuDescription: sku.description || "",
                  oldPrice: sku.old_price ? String(sku.old_price) : "",
                  currentPrice: String(sku.current_price || ""),
                  isActive: sku.is_active,
                  isWholesale: normalizeSkuWholesale(sku.is_wholesale, nextProduct.is_wholesale),
                  showInCatalog: Boolean(sku.show_in_catalog),
                  imageFiles: [],
                  imagePreviews: [],
                  components: sku.components.map((component) => ({
                    componentType: normalizeSkuComponentType(component.component_type),
                    displayName:
                      component.display_name ||
                      [component.item_product_name, component.item_sku_label].filter(Boolean).join(" · ") ||
                      "",
                    quantity: String(component.quantity || 1),
                    comment: component.comment || "",
                  })),
                }))
              : [emptySku()]
          );
        }
        setError("");
      })
      .catch((err) => mounted && setError(err instanceof Error ? err.message : "Не удалось загрузить форму"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id, isNew]);

  const rootCategories = useMemo(
    () => categories.filter((cat) => !cat.parent_id && cat.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [categories]
  );
  const allRootCategories = useMemo(
    () => categories.filter((cat) => !cat.parent_id).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru")),
    [categories]
  );
  const categoryEditorGroups = useMemo(
    () =>
      allRootCategories.map((root) => ({
        root,
        children: categories
          .filter((cat) => cat.parent_id === root.id)
          .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru")),
      })),
    [allRootCategories, categories]
  );
  const sortedCategories = useMemo(
    () => categoryEditorGroups.flatMap((group) => [group.root, ...group.children]),
    [categoryEditorGroups]
  );
  const subcategories = useMemo(
    () => categories.filter((cat) => cat.parent_id === categoryId && cat.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [categories, categoryId]
  );
  const subcategoryRequired = subcategories.length > 0;
  const filterCategoryGroups = useMemo(
    () =>
      rootCategories.map((root) => ({
        root,
        children: categories
          .filter((cat) => cat.parent_id === root.id && cat.is_active)
          .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru")),
      })),
    [categories, rootCategories]
  );
  const updateSku = (idx: number, patch: Partial<SkuForm>) => {
    setSkus((prev) => prev.map((sku, i) => (i === idx ? { ...sku, ...patch } : sku)));
  };

  const toggleFilterCategory = (categoryId: string) => {
    setFilterCategoryIds((prev) => (prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]));
  };

  const toggleHealthFilter = (healthFilterId: string) => {
    setHealthFilterIds((prev) => (prev.includes(healthFilterId) ? prev.filter((id) => id !== healthFilterId) : [...prev, healthFilterId]));
  };

  const openCategoryCreator = () => {
    setCategoryCreatorOpen((open) => !open);
    setCategoryCreatorError("");
    setNewCategoryLevel(categoryId ? "child" : "root");
    setNewCategoryParentId(categoryId || rootCategories[0]?.id || "");
  };

  const resetCategoryCreator = () => {
    setNewCategoryName("");
    setNewCategorySlug("");
    setCategoryCreatorError("");
  };

  const selectCategoryForEditing = (category: Category) => {
    setEditingCategoryId(category.id);
    setEditCategoryLevel(category.parent_id ? "child" : "root");
    setEditCategoryParentId(category.parent_id || "");
    setEditCategoryName(category.name);
    setEditCategorySlug(category.slug);
    setEditCategoryActive(category.is_active);
    setCategoryEditorError("");
  };

  const openCategoryEditor = () => {
    setCategoryEditorOpen((open) => {
      const nextOpen = !open;
      if (nextOpen && !editingCategoryId && sortedCategories[0]) {
        selectCategoryForEditing(sortedCategories[0]);
      }
      return nextOpen;
    });
    setCategoryEditorError("");
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    const parentId = newCategoryLevel === "child" ? newCategoryParentId : null;
    if (!name) {
      setCategoryCreatorError("Укажите название категории");
      return;
    }
    if (newCategoryLevel === "child" && !parentId) {
      setCategoryCreatorError("Выберите категорию 1 уровня");
      return;
    }

    setCreatingCategory(true);
    setCategoryCreatorError("");
    try {
      const created = await api.createCategory({
        parent_id: parentId,
        name,
        slug: newCategorySlug.trim() || null,
        is_active: true,
      });
      setCategories((prev) => [...prev.filter((category) => category.id !== created.id), created]);
      if (created.parent_id) {
        setCategoryId(created.parent_id);
        setSubcategoryId(created.id);
      } else {
        setCategoryId(created.id);
        setSubcategoryId("");
      }
      resetCategoryCreator();
      setCategoryCreatorOpen(false);
    } catch (err) {
      setCategoryCreatorError(err instanceof Error ? err.message : "Не удалось создать категорию");
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    const name = editCategoryName.trim();
    const parentId = editCategoryLevel === "child" ? editCategoryParentId : null;
    if (!editingCategoryId) {
      setCategoryEditorError("Выберите категорию");
      return;
    }
    if (!name) {
      setCategoryEditorError("Укажите название категории");
      return;
    }
    if (editCategoryLevel === "child" && !parentId) {
      setCategoryEditorError("Выберите категорию 1 уровня");
      return;
    }

    setUpdatingCategory(true);
    setCategoryEditorError("");
    try {
      const updated = await api.updateCategory(editingCategoryId, {
        parent_id: parentId,
        name,
        slug: editCategorySlug.trim() || null,
        is_active: editCategoryActive,
      });
      setCategories((prev) => prev.map((category) => (category.id === updated.id ? updated : category)));
      if (!updated.is_active) {
        if (categoryId === updated.id) {
          setCategoryId("");
          setSubcategoryId("");
        }
        if (subcategoryId === updated.id) {
          setSubcategoryId("");
        }
        setFilterCategoryIds((prev) => prev.filter((id) => id !== updated.id));
      } else if (categoryId === updated.id && updated.parent_id) {
        setCategoryId(updated.parent_id);
        setSubcategoryId(updated.id);
      } else if (subcategoryId === updated.id && updated.parent_id) {
        setCategoryId(updated.parent_id);
      }
      selectCategoryForEditing(updated);
    } catch (err) {
      setCategoryEditorError(err instanceof Error ? err.message : "Не удалось обновить категорию");
    } finally {
      setUpdatingCategory(false);
    }
  };

  const updateSkuComponent = (skuIdx: number, componentIdx: number, patch: Partial<SkuComponentForm>) => {
    setSkus((prev) =>
      prev.map((sku, i) =>
        i === skuIdx
          ? {
              ...sku,
              components: sku.components.map((component, j) => (j === componentIdx ? { ...component, ...patch } : component)),
            }
          : sku
      )
    );
  };

  const removeSku = (idx: number) => {
    setSkus((prev) => {
      if (prev.length === 1) return prev;
      prev[idx]?.imagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
      return prev.filter((_, i) => i !== idx);
    });
  };

  const appendSkuComponent = (idx: number, componentType: SkuComponentType) => {
    setSkus((prev) =>
      prev.map((sku, i) =>
        i === idx ? { ...sku, components: [...sku.components, emptySkuComponent(componentType)] } : sku
      )
    );
  };

  const removeSkuComponent = (skuIdx: number, componentIdx: number) => {
    setSkus((prev) =>
      prev.map((sku, i) =>
        i === skuIdx ? { ...sku, components: sku.components.filter((_, j) => j !== componentIdx) } : sku
      )
    );
  };

  const toggleSkuComponentType = (skuIdx: number, componentType: SkuComponentType, enabled: boolean) => {
    setSkus((prev) =>
      prev.map((sku, i) => {
        if (i !== skuIdx) return sku;
        if (enabled) {
          return {
            ...sku,
            components: [...sku.components, emptySkuComponent(componentType)],
          };
        }
        return {
          ...sku,
          components: sku.components.filter((component) => component.componentType !== componentType),
        };
      })
    );
  };

  const appendSkuImageFiles = (skuIdx: number, files: File[]) => {
    setSkus((prev) =>
      prev.map((sku, i) => {
        if (i !== skuIdx) return sku;
        const seen = new Set(sku.imageFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
        const imageFiles = [...sku.imageFiles];
        const imagePreviews = [...sku.imagePreviews];
        for (const file of files) {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          if (!seen.has(key)) {
            seen.add(key);
            imageFiles.push(file);
            imagePreviews.push({ name: file.name, url: URL.createObjectURL(file) });
          }
        }
        return { ...sku, imageFiles, imagePreviews };
      })
    );
  };

  const removeSkuImageFile = (skuIdx: number, imageIdx: number) => {
    setSkus((prev) =>
      prev.map((sku, i) => {
        if (i !== skuIdx) return sku;
        const preview = sku.imagePreviews[imageIdx];
        if (preview) URL.revokeObjectURL(preview.url);
        return {
          ...sku,
          imageFiles: sku.imageFiles.filter((_, j) => j !== imageIdx),
          imagePreviews: sku.imagePreviews.filter((_, j) => j !== imageIdx),
        };
      })
    );
  };

  const appendPdfFiles = (files: File[]) => {
    setPdfFiles((prev) => {
      const seen = new Set(prev.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const next = [...prev];
      for (const file of files) {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          next.push(file);
        }
      }
      return next;
    });
  };

  const deleteProductImage = async (skuId: string, imageId: string) => {
    if (!product) return;
    setError("");
    setDeletingImageIds((prev) => new Set(prev).add(imageId));
    try {
      await api.deleteProductSkuImage(product.product_id, skuId, imageId);
      setProduct((prev) =>
        prev
          ? {
              ...prev,
              images: prev.images.filter((image) => image.id !== imageId),
              skus: prev.skus.map((sku) =>
                sku.sku_id === skuId ? { ...sku, images: (sku.images || []).filter((image) => image.id !== imageId) } : sku
              ),
            }
          : prev
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось удалить фото");
    } finally {
      setDeletingImageIds((prev) => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }
  };

  const buildPayload = (): ProductPayload | null => {
    if (!name.trim()) {
      setError("Укажите название товара");
      return null;
    }
    if (!categoryId) {
      setError("Выберите категорию");
      return null;
    }
    if (subcategoryRequired && !subcategoryId) {
      setError("Выберите подкатегорию");
      return null;
    }

    const payloadSkus: ProductSkuPayload[] = [];
    for (const [idx, sku] of skus.entries()) {
      const currentPrice = toNumberOrNull(sku.currentPrice);
      if (currentPrice === null || currentPrice < 0) {
        setError(`Укажите цену для SKU ${idx + 1}`);
        return null;
      }
      const components: ProductSkuPayload["components"] = [];
      for (const [componentIdx, component] of sku.components.entries()) {
        const quantity = toNumberOrNull(component.quantity);
        if (quantity === null || quantity < 1) {
          setError(`Укажите количество для компонента ${componentIdx + 1} в SKU ${idx + 1}`);
          return null;
        }
        if (!component.displayName.trim()) {
          setError(`Укажите название компонента ${componentIdx + 1} в SKU ${idx + 1}`);
          return null;
        }
        components.push({
          component_type: component.componentType,
          item_product_id: null,
          item_sku_id: null,
          item_sku_index: null,
          display_name: component.displayName.trim(),
          quantity,
          comment: component.comment.trim() || null,
          sort_order: (componentIdx + 1) * 10,
        });
      }
      payloadSkus.push({
        sku_id: sku.skuId,
        weight_label: sku.weightLabel.trim() || null,
        weight_grams: toNumberOrNull(sku.weightGrams),
        package_label: null,
        description: sku.skuDescription.trim() || null,
        old_price: toNumberOrNull(sku.oldPrice),
        current_price: currentPrice,
        is_active: sku.isActive,
        is_wholesale: sku.isWholesale,
        show_in_catalog: sku.showInCatalog,
        sort_order: (idx + 1) * 10,
        components,
      });
    }

    return {
      category_id: categoryId,
      subcategory_id: subcategoryId || null,
      product_type: "single",
      name: name.trim(),
      slug: slug.trim() || null,
      content_block: contentBlock.trim() || null,
      is_bestseller: isBestseller,
      is_wholesale: false,
      status,
      filter_category_ids: filterCategoryIds.filter((id) => id !== categoryId && id !== subcategoryId),
      health_filter_ids: healthFilterIds,
      skus: payloadSkus,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      const saved = isNew ? await api.createProduct(payload) : await api.updateProduct(id || "", payload);
      const savedSkus = [...saved.skus].sort((a, b) => a.sort_order - b.sort_order);
      for (const [skuIndex, sku] of skus.entries()) {
        if (sku.imageFiles.length === 0) continue;
        const savedSku = sku.skuId ? savedSkus.find((item) => item.sku_id === sku.skuId) : savedSkus[skuIndex];
        if (!savedSku) {
          throw new Error(`Не удалось определить сохраненный SKU ${skuIndex + 1} для загрузки фото`);
        }
        await api.uploadProductSkuImages(saved.product_id, savedSku.sku_id, sku.imageFiles);
      }
      for (const file of pdfFiles) {
        await api.uploadProductFile(saved.product_id, file, file.name);
      }
      navigate("/catalog");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сохранить товар");
    } finally {
      setSaving(false);
    }
  };

  const existingImagesForSku = (sku: SkuForm): ProductImage[] => {
    if (!sku.skuId) return [];
    return product?.skus.find((item) => item.sku_id === sku.skuId)?.images || [];
  };

  if (loading) {
    return <div className="rounded-lg border border-[#E8E2D8] bg-white p-6 text-sm text-gray-500">Загрузка...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <button
          onClick={() => navigate("/catalog")}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          Назад
        </button>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-5">
        {isNew ? "Добавить новую карточку товара" : "Редактирование товара"}
      </h2>

      {error && <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Название</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                placeholder="Липовый мёд"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                placeholder="Автоматически из названия"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Категория</label>
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubcategoryId("");
                }}
                className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
              >
                <option value="">Выберите...</option>
                {rootCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Подкатегория</label>
              <select
                value={subcategoryId}
                onChange={(e) => setSubcategoryId(e.target.value)}
                disabled={!categoryId || !subcategoryRequired}
                className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50"
              >
                <option value="">{subcategoryRequired ? "Выберите..." : "Нет подкатегорий"}</option>
                {subcategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Статус</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as "draft" | "active" | "hidden")}
                className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
              >
                <option value="draft">Черновик</option>
                <option value="active">Активен</option>
                <option value="hidden">Скрыт</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <label className="flex items-start gap-3 rounded-lg border border-[#E8E2D8] bg-white px-3 py-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isBestseller}
                onChange={(e) => setIsBestseller(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span>
                <span className="block font-medium text-gray-800">Хит продаж</span>
                <span className="block text-xs text-gray-500">Показывать товар в блоке хитов на главной.</span>
              </span>
            </label>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openCategoryCreator}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-700 hover:bg-[#F7F3ED]"
              >
                <i className={categoryCreatorOpen ? "ri-close-line" : "ri-add-line"} />
                Создать новую категорию
              </button>
              <button
                type="button"
                onClick={openCategoryEditor}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-700 hover:bg-[#F7F3ED]"
              >
                <i className={categoryEditorOpen ? "ri-close-line" : "ri-edit-line"} />
                Редактировать категории
              </button>
            </div>

            {categoryCreatorOpen ? (
              <div className="rounded-lg border border-[#E8E2D8] bg-[#FCFAF7] p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Уровень</label>
                    <select
                      value={newCategoryLevel}
                      onChange={(event) => {
                        const level = event.target.value as "root" | "child";
                        setNewCategoryLevel(level);
                        setCategoryCreatorError("");
                        if (level === "child") {
                          setNewCategoryParentId(newCategoryParentId || categoryId || rootCategories[0]?.id || "");
                        }
                      }}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
                    >
                      <option value="root">Категория 1 уровня</option>
                      <option value="child">Категория 2 уровня</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Родительская категория</label>
                    <select
                      value={newCategoryParentId}
                      onChange={(event) => {
                        setNewCategoryParentId(event.target.value);
                        setCategoryCreatorError("");
                      }}
                      disabled={newCategoryLevel === "root"}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">Выберите...</option>
                      {rootCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Название</label>
                    <input
                      value={newCategoryName}
                      onChange={(event) => {
                        setNewCategoryName(event.target.value);
                        setCategoryCreatorError("");
                      }}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
                      placeholder="Например: Наборы мыла"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Slug</label>
                    <input
                      value={newCategorySlug}
                      onChange={(event) => setNewCategorySlug(event.target.value)}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
                      placeholder="Автоматически из названия"
                    />
                  </div>
                </div>

                {categoryCreatorError ? <p className="mt-3 text-sm text-red-600">{categoryCreatorError}</p> : null}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCreateCategory}
                    disabled={creatingCategory}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    <i className={creatingCategory ? "ri-loader-4-line animate-spin" : "ri-check-line"} />
                    {creatingCategory ? "Создание..." : "Создать"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetCategoryCreator();
                      setCategoryCreatorOpen(false);
                    }}
                    disabled={creatingCategory}
                    className="rounded-lg border border-[#E8E2D8] bg-white px-4 py-2 text-sm text-gray-700 hover:bg-[#F7F3ED] disabled:opacity-60"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : null}

            {categoryEditorOpen ? (
              <div className="rounded-lg border border-[#E8E2D8] bg-[#FCFAF7] p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Категория</label>
                    <select
                      value={editingCategoryId}
                      onChange={(event) => {
                        const selected = categories.find((category) => category.id === event.target.value);
                        if (selected) selectCategoryForEditing(selected);
                      }}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500"
                    >
                      <option value="">Выберите...</option>
                      {categoryEditorGroups.map((group) => (
                        <optgroup
                          key={group.root.id}
                          label={`${group.root.name}${!group.root.is_active ? " (скрыта)" : ""}`}
                        >
                          <option value={group.root.id}>
                            {group.root.name}
                            {!group.root.is_active ? " (скрыта)" : ""}
                          </option>
                          {group.children.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              — {cat.name}
                              {!cat.is_active ? " (скрыта)" : ""}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Уровень</label>
                    <select
                      value={editCategoryLevel}
                      onChange={(event) => {
                        const level = event.target.value as "root" | "child";
                        setEditCategoryLevel(level);
                        setCategoryEditorError("");
                        if (level === "child") {
                          setEditCategoryParentId(editCategoryParentId || allRootCategories.find((cat) => cat.id !== editingCategoryId)?.id || "");
                        }
                      }}
                      disabled={!editingCategoryId}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50"
                    >
                      <option value="root">Категория 1 уровня</option>
                      <option value="child">Категория 2 уровня</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Родительская категория</label>
                    <select
                      value={editCategoryParentId}
                      onChange={(event) => {
                        setEditCategoryParentId(event.target.value);
                        setCategoryEditorError("");
                      }}
                      disabled={!editingCategoryId || editCategoryLevel === "root"}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">Выберите...</option>
                      {allRootCategories
                        .filter((cat) => cat.id !== editingCategoryId)
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                            {!cat.is_active ? " (скрыта)" : ""}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Название</label>
                    <input
                      value={editCategoryName}
                      onChange={(event) => {
                        setEditCategoryName(event.target.value);
                        setCategoryEditorError("");
                      }}
                      disabled={!editingCategoryId}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50"
                      placeholder="Название категории"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Slug</label>
                    <input
                      value={editCategorySlug}
                      onChange={(event) => setEditCategorySlug(event.target.value)}
                      disabled={!editingCategoryId}
                      className="w-full rounded-md border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 disabled:bg-gray-50"
                      placeholder="slug-kategorii"
                    />
                  </div>

                  <label className="mt-6 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={editCategoryActive}
                      onChange={(event) => setEditCategoryActive(event.target.checked)}
                      disabled={!editingCategoryId}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    Категория активна
                  </label>
                </div>

                {categoryEditorError ? <p className="mt-3 text-sm text-red-600">{categoryEditorError}</p> : null}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleUpdateCategory}
                    disabled={updatingCategory || !editingCategoryId}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    <i className={updatingCategory ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
                    {updatingCategory ? "Сохранение..." : "Сохранить категорию"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryEditorError("");
                      setCategoryEditorOpen(false);
                    }}
                    disabled={updatingCategory}
                    className="rounded-lg border border-[#E8E2D8] bg-white px-4 py-2 text-sm text-gray-700 hover:bg-[#F7F3ED] disabled:opacity-60"
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Дополнительные фильтры каталога</label>
            <div className="rounded-lg border border-[#E8E2D8] bg-white p-3">
              <div className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">Фильтры по назначению</p>
                {healthFilters.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {healthFilters.map((filter) => {
                      const checked = healthFilterIds.includes(filter.id);
                      return (
                        <label
                          key={filter.id}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                            checked
                              ? "border-emerald-500 bg-white text-emerald-800"
                              : "border-emerald-100 bg-white/70 text-gray-600 hover:border-emerald-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleHealthFilter(filter.id)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          {filter.name}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Фильтры по назначению пока не загружены</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Работают независимо от основных категорий каталога и используются для блока подбора на главной.
                </p>
              </div>
              <div className="max-h-56 overflow-y-auto pr-1 space-y-3">
                {filterCategoryGroups.map((group) => (
                  <div key={group.root.id}>
                    <p className="mb-1.5 text-xs font-semibold text-gray-500">{group.root.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {[group.root, ...group.children].map((cat) => {
                        const checked = filterCategoryIds.includes(cat.id);
                        const isPrimary = cat.id === categoryId || cat.id === subcategoryId;
                        const isRoot = cat.id === group.root.id;
                        return (
                          <label
                            key={cat.id}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                              checked || isPrimary
                                ? "border-amber-500 bg-amber-50 text-amber-800"
                                : "border-[#E8E2D8] bg-white text-gray-600 hover:border-amber-300"
                            } ${isPrimary ? "opacity-60" : "cursor-pointer"}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked || isPrimary}
                              disabled={isPrimary}
                              onChange={() => toggleFilterCategory(cat.id)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            {isRoot ? `${cat.name} целиком` : cat.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Основная категория и подкатегория участвуют в фильтрах автоматически. Здесь можно добавить категории первого уровня
                или подкатегории компонентов набора.
              </p>
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">Информационный блок</label>
              <span className="group relative inline-flex">
                <button
                  type="button"
                  aria-label="Подсказка по форматированию"
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-[#D8CFC2] bg-white text-[11px] font-semibold text-gray-500 hover:border-amber-400 hover:text-amber-700 focus:border-amber-400 focus:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-200"
                >
                  i
                </button>
                <span className="pointer-events-none absolute left-1/2 top-7 z-20 hidden w-64 -translate-x-1/2 rounded-lg border border-[#E8E2D8] bg-white p-3 text-xs text-gray-600 shadow-lg group-focus-within:block group-hover:block">
                  <span className="block font-semibold text-gray-700 mb-2">Форматирование</span>
                  <span className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 leading-4">
                    <code className="text-amber-700">**текст**</code><span className="text-gray-500">жирный</span>
                    <code className="text-amber-700">*текст*</code><span className="text-gray-500">курсив</span>
                    <code className="text-amber-700">- пункт</code><span className="text-gray-500">список</span>
                    <code className="text-amber-700">1. пункт</code><span className="text-gray-500">нумерованный</span>
                  </span>
                </span>
              </span>
            </div>
            <textarea
              value={contentBlock}
              onChange={(e) => setContentBlock(e.target.value)}
              rows={7}
              className="w-full rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-y"
              placeholder="Польза, рекомендации, применение, состав, характеристики..."
            />
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">SKU / позиции карточки</label>
            <button
              type="button"
              onClick={() => setSkus((prev) => [...prev, emptySku()])}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-[#F7F3ED]"
            >
              <i className="ri-add-line" />
              Добавить SKU
            </button>
          </div>
          <div className="space-y-3">
            {skus.map((sku, idx) => (
              <div key={idx} className="rounded-lg border border-[#E8E2D8] bg-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">SKU</span>
                  <button
                    type="button"
                    onClick={() => removeSku(idx)}
                    disabled={skus.length === 1}
                    className="ml-auto text-xs text-red-500 disabled:text-gray-300"
                  >
                    Удалить
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <input
                    value={sku.weightLabel}
                    onChange={(e) => updateSku(idx, { weightLabel: e.target.value })}
                    className="rounded-md border border-[#E8E2D8] px-3 py-2 text-sm"
                    placeholder="Граммовка: 250 г"
                  />
                  <input
                    value={sku.weightGrams}
                    onChange={(e) => updateSku(idx, { weightGrams: e.target.value })}
                    type="number"
                    min={0}
                    className="rounded-md border border-[#E8E2D8] px-3 py-2 text-sm"
                    placeholder="Вес в граммах"
                  />
                  <input
                    value={sku.oldPrice}
                    onChange={(e) => updateSku(idx, { oldPrice: e.target.value })}
                    type="number"
                    min={0}
                    className="rounded-md border border-[#E8E2D8] px-3 py-2 text-sm"
                    placeholder="Старая цена"
                  />
                  <input
                    value={sku.currentPrice}
                    onChange={(e) => updateSku(idx, { currentPrice: e.target.value })}
                    type="number"
                    min={0}
                    required
                    className="rounded-md border border-[#E8E2D8] px-3 py-2 text-sm"
                    placeholder="Текущая цена"
                  />
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sku.isActive}
                      onChange={(e) => updateSku(idx, { isActive: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    SKU активен
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <input
                      type="checkbox"
                      checked={sku.isWholesale}
                      onChange={(e) => updateSku(idx, { isWholesale: e.target.checked })}
                      className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    Оптом дешевле
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-md border border-[#D9E6D2] bg-[#F3F8EF] px-3 py-2 text-sm text-[#335927]">
                    <input
                      type="checkbox"
                      checked={sku.showInCatalog}
                      onChange={(e) => updateSku(idx, { showInCatalog: e.target.checked })}
                      className="h-4 w-4 rounded border-[#BFD1B5] text-[#5D8C3B] focus:ring-[#8FB96B]"
                    />
                    Показывать отдельной карточкой
                  </label>
                </div>
                <div className="mt-3">
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">Описание для этой карточки SKU</label>
                  <textarea
                    value={sku.skuDescription}
                    onChange={(e) => updateSku(idx, { skuDescription: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-[#E8E2D8] px-3 py-2 text-sm text-gray-800 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="Например: описание для набора из 3 банок"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Если оставить пустым, на сайте будет использоваться общее описание товара.
                  </p>
                </div>
                <div className="mt-4 border-t border-[#F0EAE1] pt-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Фотографии SKU</p>
                      <p className="text-xs text-gray-500">Эти фото будут показываться на сайте при выборе этого SKU.</p>
                    </div>
                    <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-[#D5CFC5] bg-[#F9F7F4] px-3 py-2 text-xs text-gray-600 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer">
                      <i className="ri-upload-cloud-2-line" />
                      Добавить фото SKU
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          appendSkuImageFiles(idx, Array.from(e.target.files || []));
                          e.currentTarget.value = "";
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {existingImagesForSku(sku).length > 0 ? (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {existingImagesForSku(sku).map((image) => (
                        <div key={image.id} className="relative">
                          <img src={image.public_url} alt="" className="w-20 h-20 rounded-lg object-cover border border-[#E8E2D8]" />
                          <button
                            type="button"
                            onClick={() => deleteProductImage(image.sku_id, image.id)}
                            disabled={deletingImageIds.has(image.id)}
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-500 shadow-sm ring-1 ring-red-100 hover:bg-red-50 disabled:opacity-60"
                            aria-label="Удалить фото"
                            title="Удалить фото"
                          >
                            <i className={deletingImageIds.has(image.id) ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {sku.imagePreviews.length > 0 ? (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {sku.imagePreviews.map((preview, imageIdx) => (
                        <div key={`${preview.name}-${imageIdx}`} className="relative">
                          <img src={preview.url} alt={preview.name} className="w-20 h-20 rounded-lg object-cover border border-amber-200" />
                          <button
                            type="button"
                            onClick={() => removeSkuImageFile(idx, imageIdx)}
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-500 shadow-sm ring-1 ring-red-100 hover:bg-red-50"
                            aria-label="Убрать фото"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {sku.imageFiles.length > 0 ? (
                    <p className="text-xs text-gray-500">Выбрано для SKU: {sku.imageFiles.map((file) => file.name).join(", ")}</p>
                  ) : null}
                </div>
                <div className="mt-4 border-t border-[#F0EAE1] pt-4">
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700">Наполнение SKU</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Короткое название SKU показывается в переключателе на сайте. Состав набора и подарки выводятся отдельными списками.
                    </p>
                  </div>
                  <div className="grid gap-3 xl:grid-cols-2">
                    {(["set_item", "gift"] as const).map((componentType) => {
                      const indexedComponents = sku.components
                        .map((component, componentIndex) => ({ component, index: componentIndex }))
                        .filter(({ component }) => component.componentType === componentType);
                      const isSetComposition = componentType === "set_item";

                      return (
                        <SkuComponentsEditor
                          key={componentType}
                          type={componentType}
                          title={isSetComposition ? "Добавить состав набора" : "Добавить подарки"}
                          description={
                            isSetComposition
                              ? "Позиции, которые покупатель получает внутри выбранного набора."
                              : "Дополнительные подарки к заказу. Уже сохранённые подарки находятся здесь."
                          }
                          icon={isSetComposition ? "ri-list-check-3" : "ri-gift-line"}
                          components={indexedComponents}
                          onToggle={(enabled) => toggleSkuComponentType(idx, componentType, enabled)}
                          onAdd={() => appendSkuComponent(idx, componentType)}
                          onUpdate={(componentIndex, patch) => updateSkuComponent(idx, componentIndex, patch)}
                          onRemove={(componentIndex) => removeSkuComponent(idx, componentIndex)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">PDF</label>
            {product?.files?.length ? (
              <div className="space-y-1 mb-3">
                {product.files.map((file) => (
                  <a key={file.id} href={file.public_url} target="_blank" rel="noreferrer" className="block text-sm text-amber-700 hover:text-amber-900">
                    {file.title}
                  </a>
                ))}
              </div>
            ) : null}
            <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-[#D5CFC5] bg-[#F9F7F4] px-4 py-2.5 text-sm text-gray-600 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer">
              <i className="ri-file-pdf-2-line" />
              Загрузить PDF
              <input
                type="file"
                accept="application/pdf"
                multiple
                onChange={(e) => {
                  appendPdfFiles(Array.from(e.target.files || []));
                  e.currentTarget.value = "";
                }}
                className="hidden"
              />
            </label>
            {pdfFiles.length > 0 && (
              <div className="mt-2 space-y-1 text-xs text-gray-500">
                {pdfFiles.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} className="flex items-center gap-2">
                    <span>{file.name}</span>
                    <button type="button" onClick={() => setPdfFiles((prev) => prev.filter((_, i) => i !== idx))} className="text-red-500">
                      Убрать
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
            </span>
            {saving ? "Сохранение..." : isNew ? "Создать товар" : "Сохранить изменения"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/catalog")}
            className="inline-flex items-center gap-2 rounded-lg border border-[#E8E2D8] bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-[#F7F3ED] transition-colors whitespace-nowrap"
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
}
